import React from 'react';
import { Form, Field } from 'react-final-form';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import css from './Step1Company.module.css';

const required = value => (value && value.toString().trim() ? undefined : 'This field is required');

const Step1Company = ({ nextStep, initialValues, className, rootClassName }) => {
  const validate = values => {
    const errors = {};
    if (!values.companyName?.trim()) errors.companyName = 'Company name is required';
    if (!values.businessLicense?.trim()) errors.businessLicense = 'Business license number is required';
    if (!values.registrationNumber?.trim()) errors.registrationNumber = 'Registration number is required';
    if (!values.province) errors.province = 'Please select a province';
    if (!values.city) errors.city = 'Please select a city';
    if (!values.factoryAddress?.trim()) errors.factoryAddress = 'Factory address is required';
    if (!values.primaryIndustry) errors.primaryIndustry = 'Please select a primary industry';
    return errors;
  };

  const onSubmit = values => {
    nextStep(values);
  };

  return (
    <Form
      onSubmit={onSubmit}
      validate={validate}
      initialValues={initialValues}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit} className={css.form}>

          <div className={css.header}>
            <span className={css.step}>Step 1 of 5</span>
            <h2 className={css.title}>Company Information</h2>
            <p className={css.description}>
              Please provide your company information to begin the registration process.
            </p>
          </div>

          <hr className={css.divider} />

          {/* ── Company Details ── */}
          <section className={css.section}>
            <h3 className={css.sectionTitle}>Company Details</h3>

            <div className={css.flex}>
              <div className={css.field}>
                <label className={css.label}>
                  Company Name <span className={css.required}>*</span>
                </label>
                <Field name="companyName">
                  {({ input, meta }) => (
                    <>
                      <input
                        {...input}
                        type="text"
                        className={classNames(css.input, { [css.inputError]: meta.touched && meta.error })}
                        placeholder="Enter company name"
                      />
                      {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                    </>
                  )}
                </Field>
              </div>

              <div className={css.field}>
                <label className={css.label}>
                  Business License Number <span className={css.required}>*</span>
                </label>
                <Field name="businessLicense">
                  {({ input, meta }) => (
                    <>
                      <input
                        {...input}
                        type="text"
                        className={classNames(css.input, { [css.inputError]: meta.touched && meta.error })}
                        placeholder="Enter business license number"
                      />
                      {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                    </>
                  )}
                </Field>
              </div>
            </div>

            <div className={css.fieldFull}>
              <label className={css.label}>
                Business Registration Number <span className={css.required}>*</span>
              </label>
              <Field name="registrationNumber">
                {({ input, meta }) => (
                  <>
                    <input
                      {...input}
                      type="text"
                      className={classNames(css.input, { [css.inputError]: meta.touched && meta.error })}
                      placeholder="Enter registration number"
                    />
                    {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                  </>
                )}
              </Field>
            </div>

            <div className={css.flex}>
              <div className={css.field}>
                <label className={css.label}>
                  Province <span className={css.required}>*</span>
                </label>
                <Field name="province">
                  {({ input, meta }) => (
                    <>
                      <select
                        {...input}
                        className={classNames(css.select, { [css.inputError]: meta.touched && meta.error })}
                      >
                        <option value="">Select Province</option>
                        <option value="Guangdong">Guangdong</option>
                        <option value="Beijing">Beijing</option>
                        <option value="Shanghai">Shanghai</option>
                        <option value="Zhejiang">Zhejiang</option>
                        <option value="Jiangsu">Jiangsu</option>
                        <option value="Shandong">Shandong</option>
                        <option value="Fujian">Fujian</option>
                      </select>
                      {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                    </>
                  )}
                </Field>
              </div>

              <div className={css.field}>
                <label className={css.label}>
                  City <span className={css.required}>*</span>
                </label>
                <Field name="city">
                  {({ input, meta }) => (
                    <>
                      <select
                        {...input}
                        className={classNames(css.select, { [css.inputError]: meta.touched && meta.error })}
                      >
                        <option value="">Select City</option>
                        <option value="Shenzhen">Shenzhen</option>
                        <option value="Guangzhou">Guangzhou</option>
                        <option value="Dongguan">Dongguan</option>
                        <option value="Foshan">Foshan</option>
                        <option value="Hangzhou">Hangzhou</option>
                        <option value="Nanjing">Nanjing</option>
                        <option value="Xiamen">Xiamen</option>
                      </select>
                      {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                    </>
                  )}
                </Field>
              </div>
            </div>

            <div className={css.fieldFull}>
              <label className={css.label}>
                Factory Address <span className={css.required}>*</span>
              </label>
              <Field name="factoryAddress">
                {({ input, meta }) => (
                  <>
                    <textarea
                      {...input}
                      rows={3}
                      className={classNames(css.textarea, { [css.inputError]: meta.touched && meta.error })}
                      placeholder="Enter complete factory address"
                    />
                    {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                  </>
                )}
              </Field>
            </div>
          </section>

          {/* ── Industry Information ── */}
          <section className={css.section}>
            <h3 className={css.sectionTitle}>Industry Information</h3>

            <div className={css.flex}>
              <div className={css.field}>
                <label className={css.label}>
                  Primary Industry <span className={css.required}>*</span>
                </label>
                <Field name="primaryIndustry">
                  {({ input, meta }) => (
                    <>
                      <select
                        {...input}
                        className={classNames(css.select, { [css.inputError]: meta.touched && meta.error })}
                      >
                        <option value="">Select Primary Industry</option>
                        <option value="Electronics">Electronics</option>
                        <option value="Furniture">Furniture</option>
                        <option value="Textile">Textile</option>
                        <option value="Machinery">Machinery</option>
                        <option value="Automotive">Automotive</option>
                        <option value="Chemical">Chemical</option>
                        <option value="Food & Beverage">Food & Beverage</option>
                      </select>
                      {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                    </>
                  )}
                </Field>
              </div>

              <div className={css.field}>
                <label className={css.label}>Secondary Industry</label>
                <Field name="secondaryIndustry">
                  {({ input }) => (
                    <select {...input} className={css.select}>
                      <option value="">Select Secondary Industry</option>
                      <option value="Packaging">Packaging</option>
                      <option value="Plastic">Plastic</option>
                      <option value="Metal">Metal</option>
                      <option value="Wood">Wood</option>
                    </select>
                  )}
                </Field>
              </div>
            </div>
          </section>

          <div className={css.infoBox}>
            <h4>Next Step:</h4>
            <p>Add contact person details who will be the primary point of contact for this registration.</p>
          </div>

          <div className={css.buttonContainer}>
            <button type="submit" className={css.button}>
              Next : Contact Person →
            </button>
          </div>

        </form>
      )}
    />
  );
};

Step1Company.defaultProps = {
  className: null,
  rootClassName: null,
  initialValues: {},
};

Step1Company.propTypes = {
  className: PropTypes.string,
  rootClassName: PropTypes.string,
  nextStep: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
};

export default Step1Company;
